import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw, Search, Download, Trash2, CheckCircle, AlertTriangle,
  XCircle, Info, ChevronDown, ChevronRight, Activity, Heart, Zap,
  Filter, Clock, Server
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/PermissionsContext';

// ─── Config niveaux ──────────────────────────────────────────
const levelConfig = {
  INFO: { label: 'Information', icon: Info, color: 'text-blue-700 bg-blue-50 border-blue-100', barColor: 'bg-blue-400' },
  WARNING: { label: 'Attention', icon: AlertTriangle, color: 'text-amber-700 bg-amber-50 border-amber-100', barColor: 'bg-amber-400' },
  ERROR: { label: 'Erreur', icon: XCircle, color: 'text-red-700 bg-red-50 border-red-100', barColor: 'bg-red-500' },
  CRITICAL: { label: 'Critique', icon: XCircle, color: 'text-red-800 bg-red-100 border-red-200', barColor: 'bg-red-700' },
  DEBUG: { label: 'Debug', icon: Zap, color: 'text-gray-500 bg-gray-50 border-gray-100', barColor: 'bg-gray-300' },
};

const logSourceLabels = {
  backend: 'Application principale',
  error: 'Erreurs uniquement',
  backup: 'Sauvegardes',
  security: 'Securite & Authentification',
  access: 'Acces aux pages',
  supervisor_backend_out: 'Serveur backend',
  supervisor_backend_err: 'Erreurs serveur',
  supervisor_frontend_out: 'Serveur frontend',
};

// ─── Simplifier les messages ─────────────────────────────────
const simplifyMessage = (msg) => {
  if (!msg) return '';
  let s = msg.replace(/^[\w.]+\s*-\s*/, '');
  s = s
    .replace(/Application startup complete/gi, 'Application demarree avec succes')
    .replace(/Started server process \[(\d+)\]/gi, 'Serveur demarre (processus $1)')
    .replace(/Waiting for application startup/gi, 'Demarrage en cours...')
    .replace(/Uvicorn running on .*/gi, 'Serveur web en ecoute')
    .replace(/WatchFiles detected changes/gi, 'Fichier modifie, rechargement')
    .replace(/Started reloader process/gi, 'Surveillance des fichiers activee')
    .replace(/connection refused/gi, 'connexion refusee')
    .replace(/timeout/gi, 'delai depasse')
    .replace(/unauthorized/gi, 'acces non autorise')
    .replace(/not found/gi, 'element introuvable')
    .replace(/internal server error/gi, 'erreur interne du serveur')
    .replace(/database connection/gi, 'connexion base de donnees')
    .replace(/ModuleNotFoundError: No module named '(\w+)'/gi, 'Module Python manquant : $1')
    .replace(/ImportError/gi, 'Erreur d\'import de module');
  return s;
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return timestamp;
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return timestamp; }
};

// ─── Détecter les erreurs récurrentes ────────────────────────
const detectRecurringErrors = (logs) => {
  const errorMessages = {};
  logs.forEach((log) => {
    if (log.level === 'ERROR' || log.level === 'CRITICAL') {
      const key = (log.message || '').substring(0, 80);
      if (!errorMessages[key]) errorMessages[key] = { count: 0, message: simplifyMessage(log.message) };
      errorMessages[key].count++;
    }
  });
  return Object.values(errorMessages).filter((e) => e.count > 1).sort((a, b) => b.count - a.count);
};

const SystemLogs = () => {
  const { hasPermission } = usePermissions();
  const [selectedLogType, setSelectedLogType] = useState('backend');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [lineCount, setLineCount] = useState(100);
  const [stats, setStats] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ lines: lineCount.toString() });
      if (levelFilter) params.append('level', levelFilter);
      if (searchTerm) params.append('search', searchTerm);
      const response = await api.get(`/logs/read/${selectedLogType}?${params}`);
      setLogs(response.data.logs || []);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const r = await api.get('/logs/stats'); setStats(r.data); } catch {}
  };

  const fetchUptime = async () => {
    try { const r = await api.get('/system/uptime'); setUptime(r.data); } catch {}
  };

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchLogs, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, selectedLogType, lineCount, levelFilter, searchTerm]);

  useEffect(() => { fetchStats(); fetchUptime(); }, []);
  useEffect(() => { fetchLogs(); }, [selectedLogType]);

  const handleClearLog = async () => {
    if (!window.confirm('Voulez-vous vider ce journal ? Une copie sera conservee.')) return;
    try {
      await api.delete(`/logs/clear/${selectedLogType}`);
      toast.success('Journal vide (copie de sauvegarde creee)');
      fetchLogs();
    } catch { toast.error('Erreur lors du vidage'); }
  };

  const handleDownload = () => {
    const text = logs.map((l) => l.raw ? l.message : JSON.stringify(l)).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `logs_${selectedLogType}_${new Date().toISOString().split('T')[0]}.log`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ─── Computed ──────────────────────────────────────────────
  const recurringErrors = useMemo(() => detectRecurringErrors(logs), [logs]);
  
  // Timeline bar data
  const timelineData = useMemo(() => {
    if (!stats?.logs_by_level) return null;
    const levels = stats.logs_by_level;
    const total = Object.values(levels).reduce((s, v) => s + v, 0);
    if (total === 0) return null;
    return {
      total,
      segments: [
        { level: 'ERROR', count: (levels.ERROR || 0) + (levels.CRITICAL || 0), color: 'bg-red-500' },
        { level: 'WARNING', count: levels.WARNING || 0, color: 'bg-amber-400' },
        { level: 'INFO', count: levels.INFO || 0, color: 'bg-blue-400' },
        { level: 'DEBUG', count: levels.DEBUG || 0, color: 'bg-gray-300' },
      ].filter((s) => s.count > 0),
    };
  }, [stats]);

  const errorCount = stats?.logs_by_level?.ERROR || 0;
  const warningCount = stats?.logs_by_level?.WARNING || 0;
  const healthScore = errorCount === 0 && warningCount === 0 ? 'excellent' : errorCount === 0 ? 'bon' : errorCount < 5 ? 'attention' : 'critique';
  const healthCfg = {
    excellent: { label: 'Tout va bien', desc: 'Aucune erreur detectee', color: 'text-green-700 bg-green-50 border-green-200', icon: Heart },
    bon: { label: 'Bon etat', desc: 'Quelques avertissements mineurs', color: 'text-blue-700 bg-blue-50 border-blue-200', icon: CheckCircle },
    attention: { label: 'A surveiller', desc: 'Des erreurs ont ete detectees', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: AlertTriangle },
    critique: { label: 'Action requise', desc: 'Plusieurs erreurs critiques', color: 'text-red-700 bg-red-50 border-red-200', icon: XCircle },
  };
  const health = healthCfg[healthScore];
  const HealthIcon = health.icon;

  if (!hasPermission('settings', 'read')) {
    return <div className="p-8 text-center text-gray-500">Acces non autorise.</div>;
  }

  return (
    <div className="space-y-5" data-testid="system-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sante du systeme</h2>
          <p className="text-gray-500 text-sm mt-1">Surveillance et diagnostic de l'application</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setAutoRefresh(!autoRefresh)}
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
          >
            <Activity className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {autoRefresh ? 'Temps reel ON' : 'Temps reel'}
          </Button>
          <Button onClick={() => { fetchLogs(); fetchStats(); fetchUptime(); }} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Alertes contextuelles - erreurs récurrentes */}
      {recurringErrors.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="py-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-800">Erreurs recurrentes detectees</p>
                <ul className="mt-1 space-y-0.5">
                  {recurringErrors.slice(0, 3).map((err, i) => (
                    <li key={i} className="text-xs text-red-700">
                      {err.message.substring(0, 100)} — <strong>{err.count} occurrences</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Health + Stats + Uptime */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className={`border ${health.color}`}>
            <CardContent className="py-4 flex items-center gap-3">
              <HealthIcon className="h-7 w-7 flex-shrink-0" />
              <div>
                <div className="font-bold text-sm">{health.label}</div>
                <p className="text-[11px] opacity-70">{health.desc}</p>
              </div>
            </CardContent>
          </Card>
          <Card><CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-red-600">{errorCount}</div>
            <p className="text-xs text-gray-500 mt-1">Erreurs</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{warningCount}</div>
            <p className="text-xs text-gray-500 mt-1">Avertissements</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 text-center">
            <div className="text-2xl font-bold">{stats.total_log_files}</div>
            <p className="text-xs text-gray-500 mt-1">Journaux actifs</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 flex items-center gap-3 justify-center">
            <Server className="h-5 w-5 text-gray-400" />
            <div>
              <div className="font-bold text-sm">{uptime?.uptime_text || '...'}</div>
              <p className="text-xs text-gray-500">En ligne depuis</p>
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Timeline bar */}
      {timelineData && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-medium text-gray-500">Repartition des evenements</span>
              <span className="text-xs text-gray-400">({timelineData.total} total)</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden flex bg-gray-100">
              {timelineData.segments.map((seg) => (
                <div
                  key={seg.level}
                  className={`${seg.color} transition-all`}
                  style={{ width: `${(seg.count / timelineData.total) * 100}%` }}
                  title={`${levelConfig[seg.level]?.label || seg.level}: ${seg.count}`}
                />
              ))}
            </div>
            <div className="flex gap-4 mt-2">
              {timelineData.segments.map((seg) => (
                <div key={seg.level} className="flex items-center gap-1.5">
                  <div className={`h-2.5 w-2.5 rounded-full ${seg.color}`} />
                  <span className="text-[11px] text-gray-500">{levelConfig[seg.level]?.label || seg.level} ({seg.count})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Source + Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={selectedLogType} onValueChange={setSelectedLogType}>
          <SelectTrigger className="w-56" data-testid="log-source-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(logSourceLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600">
          <Filter className="h-3 w-3" />
          Filtres
          {showFilters ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>

        <div className="flex gap-1.5 ml-auto">
          <Button onClick={handleDownload} variant="outline" size="sm" title="Telecharger"><Download className="h-4 w-4" /></Button>
          {hasPermission('settings', 'delete') && (
            <Button onClick={handleClearLog} variant="outline" size="sm" className="text-red-500" title="Vider"><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      </div>

      {showFilters && (
        <Card>
          <CardContent className="py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select value={levelFilter || 'all'} onValueChange={(v) => setLevelFilter(v === 'all' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Niveau" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  <SelectItem value="ERROR">Erreurs</SelectItem>
                  <SelectItem value="WARNING">Avertissements</SelectItem>
                  <SelectItem value="INFO">Informations</SelectItem>
                </SelectContent>
              </Select>
              <Select value={lineCount.toString()} onValueChange={(v) => setLineCount(parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50 entrees</SelectItem>
                  <SelectItem value="100">100 entrees</SelectItem>
                  <SelectItem value="200">200 entrees</SelectItem>
                  <SelectItem value="500">500 entrees</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative col-span-2">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                  className="pl-8"
                  data-testid="log-search-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{logSourceLabels[selectedLogType]}</CardTitle>
            <span className="text-xs text-gray-400">{logs.length} entrees</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-12 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                Chargement...
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle className="h-10 w-10 mx-auto mb-3" />
                Aucune entree — tout fonctionne normalement
              </div>
            ) : (
              logs.map((log, index) => {
                const lcfg = levelConfig[log.level] || levelConfig.INFO;
                const LevelIcon = lcfg.icon;
                const isExpanded = expandedIndex === index;
                const message = simplifyMessage(log.message);

                return (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${lcfg.color}`}
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    data-testid={`system-log-${index}`}
                  >
                    <LevelIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">{lcfg.label}</Badge>
                        {log.timestamp && <span className="text-[11px] text-gray-400">{formatTime(log.timestamp)}</span>}
                      </div>
                      <p className="text-sm mt-1 break-words">{message}</p>

                      {isExpanded && (
                        <div className="mt-2 pt-2 border-t border-current/10 text-xs space-y-1 opacity-80">
                          {log.logger && <p>Source : {log.logger}</p>}
                          {log.module && <p>Module : {log.module}</p>}
                          {log.message !== message && <p>Message original : {log.message}</p>}
                          {log.exception && (
                            <details open={false}>
                              <summary className="text-red-600 cursor-pointer font-medium">Details techniques</summary>
                              <pre className="mt-1 p-2 bg-white/50 rounded text-[11px] overflow-x-auto whitespace-pre-wrap">{log.exception}</pre>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronRight className={`h-4 w-4 opacity-40 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemLogs;
